import React, { useEffect, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import { Box, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import buildQuery from "../../utils/query-builder";
import { returnService } from "../../utils/services/returnService";
import Returns from "../../components/returns";
import ReturnSyncModal from "../../components/return-sync-modal";
import { returnFilterInitialState } from "../../utils/filter-config";

/**
 * Returns list page.
 *
 * Provider-neutral: `api/return` holds one row per (client, oms_provider,
 * external_id) and both the Pipe17 and Shopify pipelines write into it, so this
 * page renders whatever the client has without branching. The "Sync Returns"
 * action is provider-neutral too: returnService.syncReturns reads the client's
 * oms-configs and triggers the backfill on each provider that exposes a return
 * history (Pipe17, Acenda), so nothing here names a provider.
 *
 * Mirrors the Shipments page's reducer/pagination/filter wiring exactly — same
 * DashboardContainer shell, same buildQuery, same abortable fetch.
 */
function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    default:
      return state;
  }
}

const ReturnsPage = () => {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    returnFilterInitialState
  );
  const [returnList, setReturnList] = useState([]);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState(null);

  const fetchReturns = async (signal) => {
    setLoading(true);
    try {
      const response = await returnService.fetch(
        clientFilter?._id,
        buildQuery(filterData, page),
        { signal }
      );
      setReturnList(response.data || []);
      setDataCount(response?.total || 0);
    } catch (error) {
      // An aborted request is the NORMAL path here — every filter/page change
      // cancels the in-flight fetch — so it must not surface as a failure.
      if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
        console.error("Fetch failed:", error);
        setReturnList([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchReturns(controller.signal);
    return () => controller.abort();
  }, [page, filterData, clientFilter]);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  /**
   * Trigger the returns backfill for the client chosen in the modal.
   *
   * Fire-and-forget end to end: the response only means "the paging job was
   * queued". The returns themselves land asynchronously as their inbound
   * references drain, so the toast says so explicitly rather than implying the
   * list is already complete — an operator who expects rows immediately and
   * sees none would reasonably conclude the sync failed.
   */
  const handleSync = async ({ clientId, since, until }) => {
    setSyncing(true);
    try {
      await returnService.syncReturns(clientId, { since, until });
      setSyncOpen(false);
      toast.success(
        "Returns sync started — returns will appear as they import. Refresh in a moment."
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to start the returns sync"
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardContainer
      onChangeFilter={handleFilterChange}
      filterData={filterData}
      fields={filterData.columns}
      header={"Returns"}
      hideUploadButton
      hideAddButton
      onClickRefresh={() => fetchReturns()}
      onSelectClient={(e) => {
        setClientFilter(e);
        setPage(0);
      }}
      selectedClient={clientFilter?.name}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Returns
          data={returnList}
          dataCount={dataCount}
          page={page}
          rowsPerPage={filterData.limit}
          setRowsPerPage={(e) =>
            dispatch({ type: "UPDATE_LIMIT", payload: e })
          }
          setPage={setPage}
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          onSync={() => setSyncOpen(true)}
        />
      )}
      <ReturnSyncModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        onConfirm={handleSync}
        syncing={syncing}
      />
    </DashboardContainer>
  );
};

export default ReturnsPage;
